/**
 * CompetitionFilter plugin - multi-select filter for competitions.
 *
 * URL param: competitionId (singular, multiple values via getAll)
 * API param: competitionIds (plural)
 * SavedView field: competitionIds
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { CompetitionCount } from "../../../../../types/grants";

/**
 * Competition filter React component.
 * Renders a multi-select dropdown for competition filtering.
 */
const CompetitionFilterComponent = React.memo(
  function CompetitionFilterComponent(
    props: FilterComponentProps<string[]>
  ) {
    const { value, onChange, facets, isLoading } = props;

    const options = useMemo(
      () =>
        ((facets?.competitionCounts as CompetitionCount[]) ?? []).map((competition) => ({
          value: competition.id,
          label: competition.name,
          count: competition.count,
        })),
      [facets?.competitionCounts]
    );

    if (isLoading) {
      return React.createElement("div", {
        className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
      });
    }

    return React.createElement(
      "div",
      { className: "w-64 relative" },
      React.createElement(MultiSelectDropdown, {
        options,
        selectedValues: value,
        onChange,
        placeholder: "Wszystkie konkursy",
        ariaLabel: "Filtruj według konkursu",
      })
    );
  }
);

export const competitionFilter: FilterPlugin<string[]> = {
  key: "competition",
  label: "Konkurs",
  placement: "main",
  order: 2,
  defaultValue: [],
  Component: CompetitionFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((id) => params.append("competitionId", id));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params.getAll("competitionId");
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    filters.competitionIds = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    return filters.competitionIds ?? [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((id) => params.append("competitionIds", id));
  },
};
